import log from 'loglevel';
import { FilterDefinition } from '../models/FilterDefinition/FilterDefinition';
import { FilterSelections, RelationalOperator } from '../types/filter';

/**
 * Builds default filter selections from the filter definitions,
 * using `defaultRelationalOperator` and `defaultValue`.
 */
export const buildDefaultFilterSelections = (
  filterDefinitions: FilterDefinition[]
): FilterSelections =>
  filterDefinitions.reduce((acc, field) => {
    acc[field.id] = {
      relationalOperator:
        field.defaultRelationalOperator ?? RelationalOperator.EQUAL,
      value: field.defaultValue ?? [],
    };
    return acc;
  }, {} as FilterSelections);

export function getLimitField(filterFields: readonly FilterDefinition[]) {
  return filterFields.find(field => field.csvMetadata.fieldType === 'limit');
}

export const createError = (message: string): Error => {
  log.error(message);
  return new Error(message);
};

export const handleError = (prefix: string, error: unknown): never => {
  log.error(prefix, error);
  throw error;
};

export const convertToBoolean = (value: string | number | boolean | null) => {
  if (value === null) {
    return false;
  }
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'string') {
    return value.toLowerCase() === 'true' || value === '1';
  }
  return value === 1;
};

/**
 * Analyzes filter definitions to determine if the report is aggregate.
 */
export const isAggregateDefinition = (
  filterFields: readonly FilterDefinition[]
) => {
  const groupByFields = filterFields
    .filter(field => field.isGroupBy)
    .map(field => field.databaseMetadata?.columnName)
    .filter(Boolean) as string[];

  const aggregationFields = filterFields
    .filter(field => field.aggregation)
    .map(field => ({
      fieldName: field.csvMetadata.columnName,
      type: field.aggregation!.type,
      dbFieldName: field.databaseMetadata?.columnName || '',
    }));

  return groupByFields.length > 0 && aggregationFields.length > 0;
};
